import React, { useEffect, useState } from "react";
import { Users, Target, Award, Clock, Smartphone, Send, BarChart3 } from "lucide-react";
import { api } from "../../lib/api";
import StudentAnalyticsDrawer from "../../components/StudentAnalyticsDrawer";

export default function RosterTab({ apiBase = "/admin" }) {
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`${apiBase}/roster`);
      setRoster(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [apiBase]);

  const filtered = roster.filter((s) => {
    const t = q.toLowerCase();
    return !t || (s.email || "").toLowerCase().includes(t) || (s.name || "").toLowerCase().includes(t);
  });

  // Aggregate stats
  const total = roster.length;
  const active7 = roster.filter((s) => {
    if (!s.last_seen) return false;
    const days = (Date.now() - new Date(s.last_seen).getTime()) / (1000 * 60 * 60 * 24);
    return days <= 7;
  }).length;
  const avgScore = (() => {
    const ws = roster.filter((s) => s.avg_quiz_percentage != null);
    if (!ws.length) return null;
    return Math.round(ws.reduce((a, s) => a + s.avg_quiz_percentage, 0) / ws.length);
  })();

  const sendReminder = (s) => {
    const subject = encodeURIComponent("Reminder — your EGNS nursing studies");
    const body = encodeURIComponent(
      `Hi ${s.name || "there"},\n\nJust a quick reminder to continue your nursing studies on the EGNS platform. You've opened ${s.chapters_opened}/${s.total_chapters} chapters so far.\n\nSign in here: ${window.location.origin}\n\nKeep going — you've got this!\n\n— EGNS`
    );
    window.location.href = `mailto:${s.email}?subject=${subject}&body=${body}`;
  };

  return (
    <div className="space-y-6" data-testid="roster-tab">
      {/* Stats strip */}
      <div className="grid sm:grid-cols-3 gap-4">
        <RosterStat icon={Users} label="Total students" value={total} />
        <RosterStat icon={Target} label="Active last 7 days" value={active7} accent />
        <RosterStat icon={Award} label="Avg. class quiz score" value={avgScore == null ? "—" : `${avgScore}%`} />
      </div>

      {/* Search */}
      <div className="bg-white border border-border rounded-2xl p-4">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name or email…"
          data-testid="roster-search"
          className="w-full px-4 py-2.5 rounded-xl border border-border bg-bone focus:outline-none focus:border-sage"
        />
      </div>

      {/* Table — desktop/tablet */}
      <div className="hidden md:block bg-white border border-border rounded-2xl overflow-hidden" data-testid="roster-table">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-start px-4 py-3">Student</th>
              <th className="text-start px-4 py-3">Chapters</th>
              <th className="text-start px-4 py-3">Items viewed</th>
              <th className="text-start px-4 py-3">Quiz avg.</th>
              <th className="text-start px-4 py-3">Last seen</th>
              <th className="text-start px-4 py-3">Device</th>
              <th className="w-28"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                {q ? "No match" : "No students have signed in yet"}
              </td></tr>
            )}
            {!loading && filtered.map((s) => (
              <tr
                key={s.user_id}
                className="border-t border-border hover:bg-bone/60 cursor-pointer"
                data-testid={`roster-row-${s.user_id}`}
                onClick={() => setSelectedId(s.user_id)}
              >
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {s.picture ? (
                      <img src={s.picture} alt={s.name} className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-sage/10 text-sage text-xs font-semibold flex items-center justify-center">
                        {(s.name || s.email || "?")[0].toUpperCase()}
                      </div>
                    )}
                    <div className="leading-tight">
                      <div className="font-medium">{s.name || "—"}</div>
                      <div className="text-xs text-muted-foreground">{s.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="inline-flex items-center gap-2">
                    <div className="w-20 h-1.5 rounded-full bg-secondary overflow-hidden">
                      <div className="h-full bg-sage" style={{ width: `${s.total_chapters ? (s.chapters_opened / s.total_chapters) * 100 : 0}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground">{s.chapters_opened}/{s.total_chapters}</span>
                  </div>
                </td>
                <td className="px-4 py-3">{s.items_viewed}</td>
                <td className="px-4 py-3">
                  {s.avg_quiz_percentage != null ? (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
                      s.avg_quiz_percentage >= 80 ? "bg-sage/10 text-sage" :
                      s.avg_quiz_percentage >= 60 ? "bg-amber-100 text-amber-700" :
                      "bg-terracotta/10 text-terracotta"
                    }`}>
                      <Award className="w-3 h-3" /> {s.avg_quiz_percentage}%
                    </span>
                  ) : <span className="text-muted-foreground text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground">
                  <div className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {s.last_seen ? relTime(s.last_seen) : "never"}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {s.device_locked ? (
                    <span className="inline-flex items-center gap-1 text-xs text-sage"><Smartphone className="w-3 h-3" /> locked</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-end">
                  <div className="inline-flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setSelectedId(s.user_id)}
                      data-testid={`view-analytics-${s.user_id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sage/10 hover:bg-sage/20 text-sage text-xs font-medium transition"
                      title="View analytics"
                    >
                      <BarChart3 className="w-3.5 h-3.5" /> Analytics
                    </button>
                    <button
                      onClick={() => sendReminder(s)}
                      data-testid={`send-reminder-${s.user_id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-border hover:bg-secondary text-xs font-medium"
                      title={`Email ${s.email}`}
                    >
                      <Send className="w-3.5 h-3.5" /> Remind
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Card list — mobile only */}
      <div className="md:hidden space-y-2.5" data-testid="roster-cards">
        {loading && <div className="text-center text-muted-foreground text-sm py-6">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center text-muted-foreground text-sm py-8 bg-white border border-border rounded-2xl">
            {q ? "No match" : "No students have signed in yet"}
          </div>
        )}
        {!loading && filtered.map((s) => (
          <div
            key={s.user_id}
            className="bg-white border border-border rounded-2xl p-4"
            data-testid={`roster-card-${s.user_id}`}
          >
            <button
              className="w-full text-start"
              onClick={() => setSelectedId(s.user_id)}
              aria-label={`Open analytics for ${s.name || s.email}`}
            >
              <div className="flex items-center gap-3">
                {s.picture ? (
                  <img src={s.picture} alt={s.name} className="w-10 h-10 rounded-full shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-sage/10 text-sage font-semibold flex items-center justify-center shrink-0">
                    {(s.name || s.email || "?")[0].toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{s.name || "—"}</div>
                  <div className="text-xs text-muted-foreground truncate">{s.email}</div>
                </div>
                {s.avg_quiz_percentage != null && (
                  <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                    s.avg_quiz_percentage >= 80 ? "bg-sage/10 text-sage" :
                    s.avg_quiz_percentage >= 60 ? "bg-amber-100 text-amber-700" :
                    "bg-terracotta/10 text-terracotta"
                  }`}>
                    <Award className="w-3 h-3" /> {s.avg_quiz_percentage}%
                  </span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px] text-muted-foreground">
                <div className="inline-flex items-center gap-1.5">
                  <div className="flex-1 h-1 rounded-full bg-secondary overflow-hidden max-w-[80px]">
                    <div className="h-full bg-sage" style={{ width: `${s.total_chapters ? (s.chapters_opened / s.total_chapters) * 100 : 0}%` }} />
                  </div>
                  <span>{s.chapters_opened}/{s.total_chapters} ch</span>
                </div>
                <div className="inline-flex items-center gap-1 justify-end">
                  <Clock className="w-3 h-3" />
                  {s.last_seen ? relTime(s.last_seen) : "never"}
                </div>
                <div>{s.items_viewed} items viewed</div>
                <div className="text-end">
                  {s.device_locked ? (
                    <span className="inline-flex items-center gap-1 text-sage"><Smartphone className="w-3 h-3" /> locked</span>
                  ) : "—"}
                </div>
              </div>
            </button>
            <div className="flex gap-2 mt-3 pt-3 border-t border-border">
              <button
                onClick={() => setSelectedId(s.user_id)}
                data-testid={`view-analytics-m-${s.user_id}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-sage/10 hover:bg-sage/20 text-sage text-xs font-medium transition"
              >
                <BarChart3 className="w-3.5 h-3.5" /> Analytics
              </button>
              <button
                onClick={() => sendReminder(s)}
                data-testid={`send-reminder-m-${s.user_id}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-border hover:bg-secondary text-xs font-medium"
              >
                <Send className="w-3.5 h-3.5" /> Remind
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="text-xs text-muted-foreground">
        Click a student row to open the full analytics drill-down. Reminders open your default email client with a pre-filled message.
      </div>

      {selectedId && (
        <StudentAnalyticsDrawer
          userId={selectedId}
          onClose={() => setSelectedId(null)}
          apiPath={`${apiBase}/students/${selectedId}/analytics`}
        />
      )}
    </div>
  );
}

function RosterStat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`rounded-2xl border p-5 flex items-center gap-4 ${accent ? "bg-gradient-to-br from-sage/8 to-terracotta/5 border-sage/20" : "bg-white border-border"}`}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent ? "bg-sage text-white" : "bg-sage/10 text-sage"}`}>
        <Icon className="w-5 h-5" strokeWidth={1.7} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">{label}</div>
        <div className="font-display text-2xl font-semibold tracking-tight">{value}</div>
      </div>
    </div>
  );
}

function relTime(iso) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}
